import { motion } from "framer-motion";

const CLIENTS = [
  { name: "Indian Oil Corporation", short: "IndianOil", sector: "Refinery & Marketing", logo: "/clients/iocl.png" },
  { name: "Bharat Petroleum", short: "BPCL", sector: "Refinery & Marketing", logo: "/clients/bpcl.png" },
  { name: "Hindustan Petroleum", short: "HPCL", sector: "Refinery & Marketing", logo: "/clients/hpcl.jpg" },
  { name: "Chennai Petroleum", short: "CPCL", sector: "Refinery", logo: "/clients/cpcl.jpg" },
  { name: "GAIL (India) Limited", short: "GAIL", sector: "Gas Transmission", logo: "/clients/gail.png" },
  { name: "Oil India Limited", short: "OIL", sector: "Exploration & Production", logo: "/clients/oil-india.png" },
  { name: "Bhabha Atomic Research Centre", short: "BARC", sector: "Atomic Research", logo: "/clients/barc.png" },
  { name: "Hindustan Organic Chemicals", short: "HOCL", sector: "Organic Chemicals", logo: "/clients/hocl.jpg" },
  { name: "Fertilisers and Chemicals Travancore", short: "FACT", sector: "Fertiliser", logo: "/clients/fact.png" },
  {
    name: "Nuclear Power Corporation of India",
    short: "NPCIL",
    sector: "Nuclear Power",
    logo: "/clients/npcil.webp",
  },
  {
    name: "Brahmaputra Cracker & Polymer",
    short: "BCPL",
    sector: "Petrochemical",
    logo: "/clients/bcpl.png",
  },
];

const Disc = ({ c, idx }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.5, delay: (idx % 6) * 0.07, ease: [0.16, 1, 0.3, 1] }}
    className="group relative flex flex-col items-center"
    data-testid={`client-${c.short.toLowerCase()}`}
  >
    <div className="relative aspect-square w-full max-w-[170px] rounded-full bg-white shadow-[0_18px_44px_-18px_rgba(15,33,55,0.22)] ring-1 ring-black/[0.03] flex items-center justify-center transition-all duration-500 group-hover:shadow-[0_26px_60px_-18px_rgba(27,95,170,0.35)] group-hover:-translate-y-1.5">
      <span className="absolute inset-0 rounded-full border border-transparent group-hover:border-brand/30 transition-colors duration-500" />
      <img
        src={c.logo}
        alt={`${c.name} logo — industrial fasteners supplied by Rahil Engineering Works`}
        loading="lazy"
        className="max-h-[60%] max-w-[70%] object-contain transition-transform duration-500 group-hover:scale-[1.07]"
      />
    </div>

    <div className="pointer-events-none mt-3 h-11 text-center opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-400">
      <div className="font-display font-bold uppercase text-[13px] text-ink leading-tight">
        {c.short}
      </div>
      <div className="font-sans font-semibold text-[11px] tracking-[0.14em] uppercase text-steel mt-1">
        {c.sector}
      </div>
    </div>
  </motion.div>
);

export default function Clients() {
  return (
    <section id="clients" className="bg-mist py-20 lg:py-24" data-testid="clients-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-16"
        >
          <div>
            <div className="flex items-center gap-3 text-[11px] sm:text-xs tracking-[0.12em] uppercase text-steel font-semibold mb-4">
              <span className="w-10 h-[2px] bg-brand" />
              Clients & Approvals
            </div>
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[1.02] text-4xl sm:text-5xl text-ink">
              Supplying India's
              <br />
              energy majors
            </h2>
          </div>
          <p className="max-w-sm text-sm text-slate-600 leading-relaxed">
            Our stud bolts, heavy hex nuts and customised fasteners go into
            refineries, gas transmission networks, atomic research
            establishments, fertiliser plants and petrochemical crackers — every
            batch released with mill test certificates.
          </p>
        </motion.div>

        <div className="space-y-4" data-testid="clients-grid">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-5 gap-y-4 justify-items-center">
            {CLIENTS.slice(0, 6).map((c, i) => (
              <Disc key={c.short} c={c} idx={i} />
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-4">
            {CLIENTS.slice(6).map((c, i) => (
              <div key={c.short} className="w-[calc(50%-0.625rem)] sm:w-[calc(33.333%-0.85rem)] lg:w-[calc(25%-0.95rem)] xl:w-[calc(16.666%-1.05rem)] flex justify-center">
                <Disc c={c} idx={i + 6} />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 font-sans font-semibold text-[11px] tracking-[0.14em] uppercase text-steel">
          <span data-testid="clients-note">
            Hover a client to see the plant sector · Mill test certificates on every batch
          </span>
          <span className="hidden sm:block w-px h-3 bg-steel-light" />
          <span>Third-party inspection supported</span>
        </div>
      </div>
    </section>
  );
}
